import { practiceBookCopy } from './practice-book-copy'

const escapeText = (value: string) => value.replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
})[character] ?? character)

/** Locally drawn SVG; only the requested scene reaches the DOM. */
export function renderPracticeBook(state: number, lang: 'ru' | 'en'): string {
  const line = (x1: number, y1: number, x2: number, y2: number, color = 'var(--practice-ink)', width = 1) =>
    `<path d="M${x1} ${y1}L${x2} ${y2}" fill="none" stroke="${color}" stroke-width="${width}"/>`;
  const text = (x: number, y: number, value: string, size = 16, color = 'var(--practice-ink)', extra = '') =>
    `<text x="${x}" y="${y}" font-size="${size}" fill="${color}" ${extra}>${escapeText(lang === 'en' ? practiceBookCopy[value] ?? value : value)}</text>`;
  const check = (x: number, y: number, size = 1, color = 'var(--color-accent)') =>
    `<path d="m${x} ${y} ${7 * size} ${7 * size} ${14 * size} ${-17 * size}" fill="none" stroke="${color}" stroke-width="${3 * size}" stroke-linecap="round" stroke-linejoin="round"/>`;

  // Eight individual, hand-drawn portrait variants, sharing a fine-line style.
  function portrait(x: number, y: number, scale = 1, variant = 0) {
    const hair = [
      'M39 110V64C36 14 109 6 123 48L133 123L110 133L110 64Q79 75 55 49L56 124Z',
      'M44 66Q37 23 73 20Q116 13 124 62L112 69L107 47Q79 57 55 43L56 70Z',
      'M34 113Q15 86 36 68Q18 39 45 29Q53 4 77 17Q108 1 120 28Q149 31 134 61Q149 90 128 111L111 89V53Q80 65 54 55V105Z',
      'M44 63Q38 24 71 21Q100 10 121 40L123 65L108 60L100 36Q80 61 54 57L54 69Z',
      'M34 122L39 56Q43 14 86 16Q128 16 132 58L138 133L111 128L112 55Q83 54 69 36Q65 62 52 68L56 123Z',
      'M44 62Q38 20 83 19Q122 21 124 63L112 64Q112 32 84 35Q54 34 55 67Z',
      'M35 95Q27 64 41 34Q57 9 92 18Q126 21 134 59L125 99L111 85V55L82 39L54 59L57 96Z',
      'M42 64Q32 28 64 22Q73 5 95 18Q127 18 124 62L113 68L110 42Q72 60 54 51L55 69Z'
    ];
    const glasses = variant % 3 === 0
      ? '<g fill="none" stroke="var(--practice-ink)" stroke-width="1.6"><rect x="55" y="70" width="23" height="17" rx="6"/><rect x="88" y="70" width="23" height="17" rx="6"/><path d="M78 76h10M50 72l5 3m56 0 5-3"/></g>' : '';
    const beard = variant === 3 || variant === 7
      ? '<path d="M55 90Q82 113 111 90L105 112Q83 130 61 111Z" fill="var(--practice-ink)"/><path d="M75 109h17" stroke="var(--practice-paper)" fill="none"/>' : '';
    const shirt = variant % 2 === 0
      ? '<path d="M30 170L39 137L65 125L83 153L103 125L132 138L140 170" fill="var(--practice-ink)"/><path d="m65 125-7 27 16-7 9 25m20-45 9 27-16-7" stroke="var(--practice-paper)" fill="none" stroke-width="1.5"/>'
      : '<path d="M27 170L37 139L65 125Q82 137 102 125L134 140L143 170" fill="var(--practice-ink)"/><path d="M65 128Q81 155 104 128" fill="none" stroke="var(--practice-paper)" stroke-width="2"/>';
    return `<g transform="translate(${x} ${y}) scale(${scale})">
      <path d="${hair[variant % 8]}" fill="var(--practice-ink)"/>
      <path d="M67 104L66 129Q82 143 103 127L100 104" fill="var(--practice-paper)" stroke="var(--practice-ink)" stroke-width="1.8"/>
      <path d="M53 60Q52 40 81 36Q111 34 114 62L111 92Q107 117 83 120Q58 115 54 94Z" fill="var(--practice-paper)" stroke="var(--practice-ink)" stroke-width="1.8"/>
      <path d="M62 70l11-2m20 0 11 3M82 77l-4 16 7 1M74 105q9 3 18-1" stroke="var(--practice-ink)" stroke-width="1.5" fill="none"/>
      <path d="M68 78v3m30-3v3" stroke="var(--practice-ink)" stroke-width="3"/>
      ${glasses}${beard}${shirt}
      <path d="M43 159l-2 11m84-11 2 11" stroke="var(--practice-paper)" stroke-width="1"/>
    </g>`;
  }

  function spread(content: string, transform: string, page = '01') {
    return `<g transform="${transform}">
      <path d="M-12 11Q165-20 330 22Q500-20 672 11L688 733Q507 704 331 744Q150 700-26 728Z" fill="var(--practice-cover)" stroke="var(--practice-cover-edge)" stroke-width="2"/>
      <path d="M-3 17Q165-10 330 31Q496-12 664 17L674 713Q497 690 331 731Q157 692-15 711Z" fill="var(--practice-paper)" stroke="var(--practice-paper-muted)"/>
      ${[0, 1, 2, 3, 4].map(i => `<path d="M-13 ${694 + i * 4}Q155 ${677 + i * 4} 331 ${716 + i * 3}Q510 ${678 + i * 4} 674 ${696 + i * 4}" fill="none" stroke="var(--practice-paper-muted)" stroke-width=".7"/>`).join('')}
      <path d="M4 0Q163-24 330 17Q497-22 660 0L671 691Q500 668 331 711Q163 672-8 693Z" fill="var(--practice-paper)" stroke="var(--practice-paper-muted)" stroke-width="1"/>
      <path d="M330 17L331 711M322 16Q308 350 323 708M338 16Q352 350 339 708" fill="none" stroke="var(--practice-paper-muted)" stroke-width=".65"/>
      <image href="/logo-ocm-kpm.png" x="30" y="25" width="10" height="22"/>
      ${text(48, 43, 'hub4psy', 14, 'var(--practice-ink)', 'font-weight="650"')}
      ${text(548, 43, 'ПРАКТИКА', 8, 'var(--practice-paper-muted)', 'letter-spacing="1.5"')}
      ${line(30, 58, 290, 58, 'var(--practice-paper-muted)', .6)}${line(371, 58, 630, 58, 'var(--practice-paper-muted)', .6)}
      ${content}
      ${text(30, 661, 'ЛИЧНОЕ СТАНОВИТСЯ ПРОФЕССИОНАЛЬНЫМ', 6.5, 'var(--practice-paper-muted)', 'letter-spacing="1.1"')}
      ${text(610, 661, page, 10, 'var(--practice-paper-muted)', 'class="art-mono"')}
      <path d="M575-9h32v137l-16-12-16 12Z" fill="var(--practice-ribbon)"/>
    </g>`;
  }

  function closed(content: string, transform: string) {
    return `<g transform="${transform}">
      <path d="M16 15H503L514 725L29 747L-11 722V20Z" fill="var(--practice-cover)" stroke="var(--practice-cover-edge)" stroke-width="1.4"/>
      <path d="M30 708L493 690V726L30 745Z" fill="var(--practice-paper)" stroke="var(--practice-paper-muted)"/>
      ${[0, 1, 2, 3, 4, 5].map(i => line(35, 714 + 4 * i, 488, 698 + 4 * i, 'var(--practice-paper-muted)', .5)).join('')}
      <path d="M-11 7Q-10-4 4-5L493-5V704L12 726Q-11 730-11 710Z" fill="var(--practice-cover)" stroke="var(--practice-cover-edge)" stroke-width="1.4"/>
      <path d="M17-3V723M22-3V722" stroke="var(--practice-cover-edge)" stroke-width=".5"/>

      <image href="/logo-ocm-kpm.png" x="58" y="36" width="15" height="32"/>
      ${text(85, 59, 'hub4psy', 22, 'var(--practice-cover-ink)', 'font-weight="600"')}
      ${text(58, 86, 'ПРОФЕССИОНАЛЬНАЯ ПРАКТИКА', 8, 'var(--practice-cover-ink)', 'letter-spacing="2.1"')}
      ${content}
      ${line(58, 641, 441, 641, 'var(--practice-cover-ink)', .5)}
      ${text(58, 665, 'ВАШЕ МЕСТО В ПРОФЕССИИ', 8, 'var(--practice-cover-ink)', 'letter-spacing="1.5"')}
      <path d="M407-5h28v149l-14-10-14 10Z" fill="var(--practice-cover-ribbon)"/>
    </g>`;
  }

  const certification = `
    ${text(375, 115, 'СЕРТИФИКАЦИЯ АЧП', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.8"')}
    ${text(374, 180, 'Практиковать.', 29)}${text(374, 220, 'Передавать.', 29)}
    ${line(375, 253, 627, 253, 'var(--practice-paper-muted)', .7)}
    ${text(373, 363, '50', 112, 'var(--practice-ink)', 'letter-spacing="-8" font-weight="450"')}${text(506, 360, '%', 63)}
    ${text(375, 395, 'ЗНАНИЯ РАБОТАЮТ ДАЛЬШЕ', 8, 'var(--practice-paper-muted)', 'letter-spacing="1.1"')}
    <circle cx="500" cy="511" r="59" fill="none" stroke="var(--practice-paper-muted)" stroke-width=".7"/>
    <circle cx="500" cy="511" r="48" fill="none" stroke="var(--practice-paper-muted)" stroke-width=".7"/>
    <path d="m491 565-7 46 19-12 15 12-5-47" fill="var(--practice-ink)"/>
    ${text(470, 506, 'АЧП', 23)}${text(469, 526, 'HUB4PSY', 8, 'var(--practice-ink)', 'letter-spacing="1"')}
    ${text(35, 107, 'ПРАВО НА ПРАКТИКУ', 9, 'var(--practice-paper-muted)', 'letter-spacing="1"')}
    <rect x="34" y="137" width="255" height="276" rx="6" fill="var(--practice-paper)" stroke="var(--practice-paper-muted)" stroke-width=".7"/>
    ${portrait(47, 139, 1.4, 0)}
    ${text(37, 463, 'Анна', 42)}${text(38, 492, 'ПСИХОЛОГ · ПРЕПОДАВАТЕЛЬ', 8, 'var(--practice-paper-muted)', 'letter-spacing="1.1"')}
    ${line(37, 526, 281, 526, 'var(--practice-paper-muted)', .7)}
    ${text(37, 554, 'Знания, у которых', 17)}${text(37, 579, 'есть продолжение.', 17)}`;

  const profile = `
    ${text(58, 170, 'Практика', 49, 'var(--practice-cover-ink)', 'letter-spacing="-1.5"')}
    ${text(58, 224, 'с вашим именем.', 39, 'var(--practice-cover-ink)', 'letter-spacing="-1.3"')}
    <rect x="60" y="268" width="216" height="226" rx="6" fill="var(--practice-paper)"/>
    ${portrait(62, 270, 1.29, 0)}
    ${text(301, 396, 'Анна', 23, 'var(--practice-cover-ink)')}
    ${text(301, 423, 'Психолог', 12, 'var(--practice-cover-ink)')}
    ${text(58, 566, 'hub4psy.com/', 18, 'var(--practice-cover-ink)', 'class="art-mono"')}
    ${text(58, 608, 'anna', 40, 'var(--practice-cover-ink)', 'class="art-mono"')}`;

  const traffic = `
    ${text(36, 115, 'ВАС УЖЕ ИЩУТ', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.4"')}
    ${['Приложение АЧП', 'Поиск · SEO', 'Ответы ИИ · GEO', 'AHM Marketing', 'AHM Blogs'].map((s, i) => `
      <circle cx="49" cy="${168 + i * 83}" r="14" fill="var(--practice-ink)"/>
      ${text(45, 172 + i * 83, String(i + 1), 10, 'var(--practice-paper)')}
      ${text(75, 173 + i * 83, s, 14)}
      <path d="M270 ${168 + i * 83}C350 ${168 + i * 83} 350 337 397 337" fill="none" stroke="var(--practice-paper-muted)" stroke-width="1"/>
    `).join('')}
    <rect x="393" y="186" width="222" height="315" rx="10" fill="var(--practice-paper)" stroke="var(--practice-ink)" stroke-width="1.5"/>
    ${portrait(417, 213, 1, 0)}${text(416, 430, 'Анна', 32)}
    ${text(416, 456, 'ВАШ ПРОФИЛЬ', 9, 'var(--practice-paper-muted)', 'letter-spacing="1"')}
    ${text(393, 549, 'В нужном месте.', 23)}${text(393, 579, 'В нужный момент.', 23)}`;

  const workspace = `
    ${text(36, 113, 'ВАША ПРАКТИКА', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.4"')}
    ${text(34, 172, 'Среда, 16', 35)}${text(36, 204, 'Рабочий день', 14, 'var(--practice-paper-muted)')}
    ${['Моделлер', 'Расписание', 'Сессии', 'Переписка', 'Моя страница'].map((s, i) => `${line(36, 251 + i * 62, 286, 251 + i * 62, 'var(--practice-paper-muted)', .5)}${text(37, 287 + i * 62, s, 17)}${text(267, 287 + i * 62, '↗', 20)}`).join('')}
    ${text(374, 115, 'СЕГОДНЯ', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.4"')}
    ${['09:00', '10:00', '11:00', '12:00', '13:00', '14:00'].map((s, i) => `${text(375, 169 + i * 72, s, 10, 'var(--practice-paper-muted)', 'class="art-mono"')}${line(424, 164 + i * 72, 633, 164 + i * 72, 'var(--practice-paper-muted)', .5)}`).join('')}
    <rect x="424" y="182" width="205" height="103" rx="6" fill="var(--practice-ink)"/>
    ${text(440, 212, 'Консультация', 16, 'var(--practice-paper)')}${text(440, 242, '10:00 — 11:00', 11, 'var(--practice-paper)', 'class="art-mono"')}
    <rect x="424" y="396" width="205" height="75" rx="6" fill="var(--practice-paper)" stroke="var(--practice-ink)"/>
    ${text(440, 427, 'Разбор случая', 15)}${text(440, 450, '13:00 — 14:00', 10)}
    ${text(374, 598, 'Всё на своём месте.', 21)}`;

  const statistics = `
    ${portrait(67, 110, 1.1, 0)}${text(45, 353, 'Анна', 43)}
    ${text(46, 388, 'ПСИХОЛОГ', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.8"')}
    ${line(46, 427, 281, 427, 'var(--practice-paper-muted)', .7)}
    ${text(46, 463, 'За числами —', 22)}${text(46, 492, 'живая практика.', 22)}
    ${text(375, 113, 'АКТИВНОСТЬ ПРАКТИКИ', 10, 'var(--practice-paper-muted)', 'letter-spacing="1"')}
    ${text(371, 222, '128', 95, 'var(--practice-ink)', 'letter-spacing="-6"')}
    ${text(377, 254, 'проведённых консультаций', 13)}
    ${[48, 89, 70, 123, 112, 154, 135, 187].map((h, i) => `<rect x="${378 + i * 31}" y="${497 - h}" width="18" height="${h}" rx="6" fill="var(--practice-ink)"/>`).join('')}
    ${line(376, 507, 629, 507, 'var(--practice-paper-muted)', .7)}
    ${text(377, 536, 'ЧИСЛА СЧИТАЕТ ПЛОЩАДКА', 9, 'var(--practice-paper-muted)', 'letter-spacing=".7"')}
    ${text(377, 599, 'Пример данных профиля', 10, 'var(--practice-paper-muted)')}`;

  const reputation = `
    ${text(35, 116, 'РЕПУТАЦИЯ', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.5"')}
    ${portrait(51, 144, 1.2, 0)}${text(40, 393, 'Анна', 42)}
    ${text(40, 429, 'Проверено площадкой', 14)}
    <circle cx="161" cy="542" r="51" fill="var(--practice-ink)"/>
    ${check(141, 543, 1.6, 'var(--practice-paper)')}
    ${text(374, 115, 'ЗА ОТЗЫВОМ ЕСТЬ СЕССИЯ', 9, 'var(--practice-paper-muted)', 'letter-spacing="1"')}
    <rect x="375" y="154" width="255" height="170" rx="10" fill="var(--practice-ink)"/>
    ${text(395, 190, 'Сессия состоялась', 17, 'var(--practice-paper)')}
    ${line(396, 218, 597, 218, 'var(--practice-paper)', .5)}
    ${text(396, 250, 'Отзыв подтверждён', 13, 'var(--practice-paper)')}${check(396, 286, .9, 'var(--practice-paper)')}
    <rect x="375" y="353" width="255" height="115" rx="10" fill="var(--practice-paper)" stroke="var(--practice-paper-muted)"/>
    ${text(395, 388, 'Сессии не было', 17)}
    <path d="m397 412 12 12m0-12-12 12" stroke="var(--color-red)" stroke-width="2"/>
    ${text(424, 425, 'Отзыв отклонён', 13)}
    ${text(375, 543, 'Доверие нельзя', 25)}${text(375, 576, 'купить.', 25)}`;

  const support = `
    ${text(35, 112, 'КРЕМНИЕВАЯ ДОЛИНА', 10, 'var(--practice-paper-muted)', 'letter-spacing="1"')}
    ${text(374, 112, 'ДУБАЙ', 10, 'var(--practice-paper-muted)', 'letter-spacing="1.5"')}
    <g fill="none" stroke="var(--practice-ink)" stroke-width="1.5">
      <path d="M30 295h265M45 293v-72h68v72m-59-59h50m-50 13h50m-50 13h50m27 34V194h95v100m-78-84h12m15 0h12m15 0h12m-66 18h12m15 0h12m15 0h12m-66 18h12m15 0h12m15 0h12M256 295v-85m-12 16 12-37 12 37m-12-13-13 23m13-23 13 23"/>
      <path d="M373 295h264m-230 0v-54h31v54m8 0V212h33v83m9 0V183h12v-29h8v-35h3v35h8v29h12v112m9 0V229h29v66m10 0v-91l43 19v72m-33-72v62m11-56v56m11-51v51"/>
    </g>
    ${portrait(68, 343, 1.03, 1)}${portrait(417, 343, 1.03, 4)}
    ${text(49, 558, 'Технологии', 24)}${text(399, 558, 'и живые люди.', 24)}
    <path d="M168 592C233 643 434 643 510 591" fill="none" stroke="var(--practice-ink)" stroke-width="1" stroke-dasharray="4 5"/>
    <circle cx="168" cy="591" r="4" fill="var(--practice-ink)"/><circle cx="510" cy="591" r="4" fill="var(--practice-ink)"/>`;

  const names = ['Анна', 'Марк', 'Лейла', 'Давид', 'Елена', 'Алексей', 'Майя', 'Артём'];
  const community = `
    ${text(35, 112, 'РАЗНЫЕ ЛЮДИ.', 23)}${text(374, 112, 'ОБЩИЙ ЯЗЫК.', 23)}
    ${names.map((name, i) => {
      const x = i % 4 < 2 ? 35 + (i % 2) * 140 : 374 + (i % 2) * 140;
      const y = i < 4 ? 162 : 380;
      return `<rect x="${x}" y="${y}" width="116" height="143" rx="6" fill="var(--practice-paper)" stroke="var(--practice-paper-muted)" stroke-width=".6"/>${portrait(x - 2, y + 6, .72, i)}${text(x, y + 167, name, 16)}${text(x, y + 185, 'ПСИХОЛОГ', 6.5, 'var(--practice-paper-muted)', 'letter-spacing="1"')}`;
    }).join('')}`;

  const scenes = [
    spread(certification, 'translate(134 105) rotate(12 350 350) skewY(-7) scale(1.17)', '01'),
    closed(profile, 'translate(293 96) rotate(13 250 360) skewY(-5) scale(1.14)'),
    spread(traffic, 'translate(130 111) rotate(-9 350 350) skewY(7) scale(1.15)', '03'),
    spread(workspace, 'translate(138 72) rotate(10 350 350) skewY(-3) scale(1.16)', '04'),
    spread(statistics, 'translate(157 84) rotate(7 350 350) skewY(-6) scale(1.16)', '05'),
    spread(reputation, 'translate(141 78) rotate(-8 350 350) skewY(6) scale(1.17)', '06'),
    spread(support, 'translate(130 100) rotate(10 350 350) skewY(-7) scale(1.17)', '07'),
    spread(community, 'translate(102 110) rotate(-7 350 350) skewY(4) scale(1.16)', '08'),
  ]
  return scenes[state] ?? scenes[0]
}
